import React, {
  useState,
  useTransition,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import {
  Layout as LayoutTemplate,
  type LayoutProps,
  type StepperStep,
} from '@tasama/ui';
import { useNavigate, useLoaderData } from 'react-router-dom';
import { FeatureFlags, isEnabled } from '@tasama/config';
import { BAContextBridge, KybTabs } from '@tasama/business-advisor';
import { useAuth } from '../../hooks/use-auth';
import { ProtectedRoute } from '../../core/router/guards/ProtectedRoute';
import {
  CompanyDocumentsUploadScreen,
  type CompanyDocumentsUploadHandle,
  FlowResultScreen,
} from '@/shared';
import CompanyDetailsEditPage, {
  type CompanyDetailsEditHandle,
} from '@/features/my-account/components/CompanyDetailsTab/CompanyDetailsEditPage';
import { useUserContextStore } from '../../stores/user-context.store';
import { useUploadCompanyDocuments } from '../../features/my-account/hooks/useMyAccountApi';
import {
  CompanyEditStep,
  COMPANY_EDIT_STEP_ROUTES,
  DocumentId,
  EntityType,
  MY_ACCOUNT_ROUTES,
  DOCUMENT_ID_TO_TYPE_MAP,
} from '../../features/my-account/constants';
import { useMultiStepFlow } from '@/hooks/use-multi-step-flow.hook';
import { COMPANY_DETAILS_STORAGE_KEY } from '@/features/my-account/hooks/useCompanyFormLogic';
import { RejectionModal } from './RejectionModal';
import { useTranslation } from '@tasama/i18n';
import type { BackendKybStatusResponse } from '../../services/kyb-status.service';
import { trackEvent } from '@tasama/utils';

interface CompanyEditLayoutProps {
  testID?: string | undefined;
  isKyb?: boolean;
  className?: string;
  onClose?: () => void;
}

const companyEditStepRoutes = COMPANY_EDIT_STEP_ROUTES;

/**
 * Company Edit Layout Component
 *
 * A specialized layout for editing company details.
 * Uses the Layout template for consistency with other layouts.
 * Provides a clean, focused interface with step navigation similar to onboarding.
 * Includes functional navigation between steps and confirmation screen.
 */
const CompanyEditLayout: React.FC<CompanyEditLayoutProps> = ({
  testID = 'company-edit-layout',
  isKyb = false,
}) => {
  const { t } = useTranslation('portal');
  const { error, errorType, clearError } = useAuth();
  const navigate = useNavigate();
  const [isPending] = useTransition();
  const [modalContent, setModalContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Get KYB status details from loader (contains rejection reason if status is REJECTED)
  const loaderData = useLoaderData() as {
    kybStatusDetails?: BackendKybStatusResponse | null;
  } | null;
  const kybStatusDetails = loaderData?.kybStatusDetails ?? null;

  // State to control KYB rejection modal visibility
  const [isKybRejectionModalOpen, setIsKybRejectionModalOpen] =
    useState<boolean>(
      // Show modal on mount if we have rejection details from loader
      kybStatusDetails !== null
    );

  const uploadDocumentsMutation = useUploadCompanyDocuments();

  // Clear any stale sessionStorage from a previous unfinished edit session so
  // the form always seeds from fresh API data when entering the edit flow.
  useEffect(() => {
    sessionStorage.removeItem(COMPANY_DETAILS_STORAGE_KEY);
  }, []);

  // Ref to the CompanyDetailsEditPage — used to call validate() and getPayload()
  // without reading from Zustand store
  const formRef = useRef<CompanyDetailsEditHandle>(null);

  // Ref to the CompanyDocumentsUploadScreen — used to call submit() and canSubmit()
  const uploadRef = useRef<CompanyDocumentsUploadHandle>(null);

  const companyEditSteps: StepperStep[] = useMemo(
    () => [
      {
        id: CompanyEditStep.COMPANY_DETAILS,
        label: t('companyEdit.companyDetailsStep'),
      },
      {
        id: CompanyEditStep.UPLOAD_DOCUMENTS,
        label: t('companyEdit.uploadDocumentsStep'),
      },
    ],
    [t]
  );

  // CompanyEditLayout has non-standard terminal steps ('confirmation', 'error') and
  // completion is tracked across multiple paths (upload callback, done handler).
  // We use the hook for internalStep state + goToNext/goToPrev navigation only;
  // completedSteps, currentStepIndex, and stepsWithCompletion stay local.
  const { internalStep, setInternalStep, goToNext, goToPrev } =
    useMultiStepFlow(companyEditSteps, CompanyEditStep.COMPANY_DETAILS);

  const stepToTab: Record<string, string | null> = {
    [CompanyEditStep.COMPANY_DETAILS]: KybTabs.COMPANY_DETAILS,
    [CompanyEditStep.UPLOAD_DOCUMENTS]: KybTabs.UPLOAD_DOCUMENTS,
    [CompanyEditStep.CONFIRMATION]: KybTabs.UPLOAD_DOCUMENTS,
    [CompanyEditStep.ERROR]: KybTabs.UPLOAD_DOCUMENTS,
  };

  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Track whether the company details step has been validated successfully
  const [isCompanyDetailsValid, setIsCompanyDetailsValid] = useState(false);

  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const [hasKybError, setHasKybError] = useState(false);
  const [hasFileUploadError, setHasFileUploadError] = useState(false);
  // Captured from formRef before CompanyDetailsEditPage unmounts on step advance
  const [companyDetailsPayload, setCompanyDetailsPayload] = useState<
    Record<string, unknown>
  >({});
  // Track whether upload step can submit
  const [canSubmitUpload, setCanSubmitUpload] = useState(false);

  const userContext = useUserContextStore((state) => state.userContext);
  const refreshUserContext = useUserContextStore(
    (state) => state.refreshUserContext
  );

  type ValidCompanyType =
    | 'INTERNATIONAL'
    | 'KSA_PRIVATE'
    | 'PIF_ESTABLISHED'
    | 'PIF_UNDER_DEVELOPMENT';
  const companyType = userContext?.companyType as ValidCompanyType;

  const handleBackToWebsite = () => {
    navigate(MY_ACCOUNT_ROUTES.DASHBOARD);
  };

  const stepValidation = useMemo(
    () => ({
      [`/${CompanyEditStep.COMPANY_DETAILS}`]: isCompanyDetailsValid,
      [`/${CompanyEditStep.UPLOAD_DOCUMENTS}`]: true,
    }),
    [isCompanyDetailsValid]
  );

  const handleDocumentUpload = async (uploadedFiles: Record<string, any[]>) => {
    setIsSubmitting(true);

    try {
      const apiPayload = companyDetailsPayload;

      const formData = new FormData();
      formData.append('companyDetails', JSON.stringify(apiPayload));

      Object.entries(uploadedFiles).forEach(([documentId, files]) => {
        files.forEach((fileData: any) => {
          if (fileData.file) {
            const documentType =
              DOCUMENT_ID_TO_TYPE_MAP[documentId] ||
              DOCUMENT_ID_TO_TYPE_MAP[DocumentId.COMPANY_REGISTRATION];

            formData.append('files', fileData.file);
            if (documentType) formData.append('documentType', documentType);
            formData.append('entityType', EntityType.KYB_ATTEMPT);
          }
        });
      });

      const response = await uploadDocumentsMutation.mutateAsync(formData);

      setUploadResponse(response);

      if (response.failureCount > 0) {
        setHasFileUploadError(true);
        return;
      }

      if (response.kybSubmitError) {
        setHasKybError(true);
        setInternalStep('error');
        return;
      }

      setCompletedSteps(
        (prev) => new Set([...prev, CompanyEditStep.UPLOAD_DOCUMENTS])
      );

      if (isKyb) {
        if (userContext?.companyType !== 'INTERNATIONAL') {
          trackEvent('kyb_initiated');
        }
      } else {
        trackEvent('company_profile_updated');
      }

      setInternalStep(CompanyEditStep.CONFIRMATION);
    } finally {
      setIsSubmitting(false);
      await refreshUserContext();
    }
  };

  const handleContinue = async () => {
    try {
      let success = false;

      if (internalStep === CompanyEditStep.COMPANY_DETAILS) {
        const isFormValid = await formRef.current?.validate();

        if (!isFormValid) {
          return;
        }

        // Capture payload now, while CompanyDetailsEditPage is still mounted.
        // formRef.current will be null after the component unmounts on step advance.
        const payload = formRef.current?.getPayload() ?? {};
        setCompanyDetailsPayload(payload);

        setIsCompanyDetailsValid(true);
        success = true;
      } else if (internalStep === CompanyEditStep.UPLOAD_DOCUMENTS) {
        // For upload documents step, trigger submit via ref
        await uploadRef.current?.submit();
        // Success is determined by the upload callback
        return;
      } else {
        success = true;
      }

      if (success) {
        setCompletedSteps((prev) => new Set([...prev, internalStep]));
        goToNext();
      }
    } catch {
      /* noop */
    }
  };

  const handleBack = goToPrev;

  const handleDone = () => {
    if (isErrorScreen) {
      setHasKybError(false);
      setHasFileUploadError(false);
      setUploadResponse(null);
      setInternalStep(CompanyEditStep.UPLOAD_DOCUMENTS);
    } else {
      setCompletedSteps(
        (prev) => new Set([...prev, CompanyEditStep.CONFIRMATION])
      );
      if (isKyb) {
        navigate(MY_ACCOUNT_ROUTES.DASHBOARD);
      } else {
        navigate(MY_ACCOUNT_ROUTES.COMPANY_EDIT);
      }
    }
  };

  const handleSetModalContent = (content: string) => setModalContent(content);
  const handleSetIsModalOpen = (open: boolean) => setIsModalOpen(open);

  const stepsWithCompletion = useMemo(() => {
    const currentStepIndex = companyEditSteps.findIndex(
      (s) => s.id === internalStep
    );
    return companyEditSteps.map((step, index) => ({
      ...step,
      completed: completedSteps.has(step.id),
      current: index === currentStepIndex,
    }));
  }, [completedSteps, internalStep]);

  const currentStepIndex = useMemo(
    () => companyEditSteps.findIndex((step) => step.id === internalStep),
    [internalStep]
  );

  const isConfirmationScreen = internalStep === CompanyEditStep.CONFIRMATION;
  const isErrorScreen = internalStep === 'error';

  const errorConfig = useMemo(() => {
    if (!error) return { show: false };
    return {
      show: true,
      data: {
        type: (errorType === 'unauthorized' ? 'unauthorized' : 'error') as
          | 'error'
          | 'unauthorized'
          | 'warning',
        message: error,
        onClear: clearError,
      },
    };
  }, [error, errorType, clearError]);

  const layoutConfig: LayoutProps = {
    variant: 'authenticated-portal',
    navigation: {
      showLanguageSwitcher: isEnabled(FeatureFlags.LANGUAGE_SWITCHER),
      onBackToWebsite: handleBackToWebsite,
      closeUrl: MY_ACCOUNT_ROUTES.COMPANY_EDIT,
    },
    sidebarContent: {
      title: t('companyEdit.businessProfileTitle'),
      description: t('companyEdit.businessProfileDescription'),
    },
    stepper: {
      routes: companyEditStepRoutes,
      steps: stepsWithCompletion,
      validation: stepValidation,
      currentStepIndex: currentStepIndex,
    },
    content: {
      outletContext: {
        setIsModalOpen: handleSetIsModalOpen,
        setModalContent: handleSetModalContent,
      },
    },
    actions: {
      onContinue: handleContinue,
      onBack: handleBack,
      onDone: handleDone,
      backText: t('actions.previous'),
      continueText:
        internalStep === CompanyEditStep.UPLOAD_DOCUMENTS
          ? t('actions.submit')
          : t('actions.next'),
      doneText: isErrorScreen ? t('actions.tryAgain') : t('actions.done'),
      isContinueLoading: isPending || isSubmitting,
      isBackDisabled: currentStepIndex === 0,
      isContinueDisabled:
        internalStep === CompanyEditStep.UPLOAD_DOCUMENTS
          ? !canSubmitUpload
          : false,
      isDoneDisabled: false,
      isDoneLoading: isPending || isSubmitting,
      showDoneButton: isConfirmationScreen || isErrorScreen,
    },
    layout: {
      maxWidth: 'max-w-[1600px]',
      padding: 'px-[62px] lg:pt-6',
      showBottomNavigation: !isErrorScreen,
      showSidebar: true,
    },
    modal: {
      isOpen: isModalOpen,
      onClose: () => setIsModalOpen(false),
      title: t('companyEdit.termsAndConditions'),
      content: modalContent,
      iconVariant: 'brand',
      headerType: 'left',
      className: 'max-h-[80vh] overflow-y-auto',
    },
    error: errorConfig,
    testID,
  };

  const renderStepComponent = () => {
    switch (internalStep) {
      case CompanyEditStep.COMPANY_DETAILS:
        return <CompanyDetailsEditPage handleRef={formRef} />;
      case CompanyEditStep.UPLOAD_DOCUMENTS:
        return (
          <CompanyDocumentsUploadScreen
            handleRef={uploadRef}
            onSubmit={handleDocumentUpload}
            companyType={companyType}
            testID={`${testID}-upload-documents`}
            uploadResponse={uploadResponse}
            hasFileUploadError={hasFileUploadError}
            isKyb={isKyb}
            onSubmitStateChange={setCanSubmitUpload}
          />
        );
      case CompanyEditStep.CONFIRMATION:
        return (
          <FlowResultScreen
            type="success"
            title={t('confirmation.thankYou')}
            description=""
            testID={`${testID}-confirmation-message`}
          />
        );
      case CompanyEditStep.ERROR:
        return (
          <FlowResultScreen
            type="error"
            title={t('companyEdit.submissionFailed')}
            description={
              hasKybError && uploadResponse?.kybSubmitError
                ? uploadResponse.kybSubmitError.message
                : t('companyEdit.submissionFailedDescription')
            }
            testID={`${testID}-error-message`}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ProtectedRoute testID={`${testID}-protection`}>
      <BAContextBridge tab={stepToTab[internalStep] ?? null} />
      {/* KYB Rejection Modal - shown when loader returns rejection details */}
      <RejectionModal
        isOpen={isKybRejectionModalOpen}
        onClose={() => setIsKybRejectionModalOpen(false)}
        statusDetails={kybStatusDetails}
        fallbackTitle={t('companyEdit.updatesRequired')}
        testID={`${testID}-kyb-rejection-modal`}
      />
      <LayoutTemplate {...layoutConfig}>{renderStepComponent()}</LayoutTemplate>
    </ProtectedRoute>
  );
};

export { CompanyEditLayout };
